import { VideoModal } from '@/components/VideoModal';
import BaseLayout from '@/layout/BaseLayout';
import { trpc, trpcClient } from '@/trpcClient';
import { campaignDisplayFormatMapper } from '@/utils/mapper';
import { useDeviceDetect } from '@/utils/useMediaQuery';
import { ListMaterialFilterTypeWithValues } from '@shelflix/schemas/material/list';
import { CampaignDto } from '@shelflix/trpc/routes/campaign/get/useCase';
import { MaterialDto } from '@shelflix/trpc/routes/material/list/useCase';
import Badge from '@shelflix/ui-web/components/Badge';
import { GenericTable } from '@shelflix/ui-web/components/GenericTable';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import { ScrollArea, ScrollBar } from '@shelflix/ui-web/components/ui/scroll-area';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { MaterialListFilter } from './MaterialListFilter';
import { MediaCard } from './MediaCard';

const formatDate = (date?: string | Date) => (date ? dayjs(date).format('DD/MM/YYYY') : '-');

const InfoItem = ({ label, value }: { label: string; value?: string }) => (
  <div className="flex flex-col gap-[5px]">
    <span className="text-xs font-semibold text-[var(--label-color)]">{label}</span>
    <span className="text-sm font-medium whitespace-pre-line text-[var(--label-color)]">{value}</span>
  </div>
);

const EditButton = ({
  navigate,
  campaign,
  ref,
}: {
  navigate: NavigateFunction;
  campaign: CampaignDto;
  ref?: Element;
}) => {
  const button = (
    <Button
      variant="outline"
      className="text-primary hover:text-primary flex h-11 gap-2.5 rounded-[5px]"
      onClick={() => navigate(`/campaign/edit/${campaign?.uuid}`)}
    >
      <Icon name="edit" />
      <span className="text-sm font-semibold">Editar</span>
    </Button>
  );

  return ref ? createPortal(button, ref) : button;
};

export function CampaignDetails() {
  const { uuid: campaignUuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();

  const { data: campaign } = useQuery(
    trpc.campaign.get.queryOptions({ uuid: campaignUuid ?? '' }, { enabled: !!campaignUuid })
  );

  const { isMobile } = useDeviceDetect();

  const [editButton, setEditButton] = useState(EditButton({ navigate, campaign }));
  const [bottomEditButton, setBottomEditButton] = useState(EditButton({ navigate, campaign }));

  const campaignHeader = useCallback(
    (node: Element) => {
      if (!isMobile) {
        setEditButton(EditButton({ navigate, campaign, ref: node }));
      } else {
        setEditButton(EditButton({ navigate, campaign, ref: undefined }));
      }
    },
    [isMobile, campaign]
  );

  const campaignMediaHeader = useCallback(
    (node: Element) => {
      if (!isMobile) {
        setBottomEditButton(EditButton({ navigate, campaign, ref: node }));
      } else {
        setBottomEditButton(EditButton({ navigate, campaign, ref: undefined }));
      }
    },
    [isMobile, campaign]
  );

  const videoTrigger = useRef(null);

  const [filters, setFilters] = useState<ListMaterialFilterTypeWithValues>({});
  const [videoUrls, setVideoUrls] = useState<string[]>([]);
  const videoTriggerRef = useRef<HTMLButtonElement | null>(null);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { data, isLoading, isError } = useQuery(
    trpc.material.list.queryOptions({
      page,
      ...(filters.search?.value && { search: { ...filters.search } }),
      ...(filters.filter && { filter: filters.filter.map((f) => ({ ...f.field })) }),
      ...(filters.sort && { sort: filters.sort }),
      campaignUuid: campaignUuid,
    })
  );

  const materials = data?.materials ?? [];
  const totalItems = data?.total ?? 0;

  const columns: Array<{
    header: string;
    key: keyof MaterialDto;
    render?: (value: MaterialDto[keyof MaterialDto], row: MaterialDto) => React.ReactNode;
  }> = [
    {
      header: 'Nome',
      key: 'name',
      render: (value, row) => (
        <div className="flex min-w-[200px] flex-col">
          <span
            className="[font-family:'Figtree',Helvetica] text-base font-medium text-[var(--gray-primary)]"
            style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}
          >
            {row.name}
          </span>
        </div>
      ),
    },
    {
      header: 'Status',
      key: 'status',
      render: (_, row) => <Badge text={row.status} color="primary" />,
    },
    {
      header: 'Campanha atual',
      key: 'currentCampaign',
      render: (_, row) => (
        <div className="[font-family:'Figtree',Helvetica] text-base text-[var(--gray-primary)]">
          {row.currentCampaign?.name}
        </div>
      ),
    },
    {
      header: 'Localização',
      key: 'facility',
      render: (_, row) => (
        <div className="[font-family:'Figtree',Helvetica] text-base text-[var(--gray-primary)]">
          {row.facility.address} {row.facility.cityName} - {row.facility.stateName}
        </div>
      ),
    },
    {
      header: 'Identificador',
      key: 'code',
      render: (value, row) => (
        <div className="flex flex-col">
          <span className="[font-family:'Figtree',Helvetica] text-base font-medium text-[var(--gray-primary)]">
            {row.code}
          </span>
        </div>
      ),
    },
    {
      header: 'Ações',
      key: 'uuid',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            className="h-8 w-8 rounded-md p-0 hover:bg-[var(--secondary)]"
            onClick={() => handleOpenVideoModal(row.uuid)}
          >
            <Icon name="live_tv" className="text-primary" />
          </Button>
          <Button
            variant="ghost"
            className="h-8 w-8 rounded-md p-0 hover:bg-[var(--secondary)]"
            onClick={() => navigate(`/pos/${row.uuid}`)}
          >
            <Icon name="featured_play_list" className="text-primary" />
          </Button>
        </div>
      ),
    },
  ];

  const handleOpenVideoModal = async (materialId: string) => {
    try {
      const response = await trpcClient.material.get.query({
        uuid: materialId,
      });
      setVideoUrls(response.currentCampaign.medias.map((item) => item.mediaUrl));
      setTimeout(() => {
        videoTriggerRef.current?.click();
      }, 0);
    } catch (err) {
      toast.error('Erro ao buscar vídeos:', err);
    }
  };

  if (!campaign)
    return (
      <BaseLayout>
        <div className="flex h-full w-full items-center justify-center">
          <span className="text-primary text-lg font-semibold">Carregando...</span>
        </div>
      </BaseLayout>
    );

  return (
    <BaseLayout>
      <div className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-8 sm:flex-row">
          <Card className="flex-1 rounded-[5px]">
            <CardContent className="flex flex-col gap-2 p-6">
              <div className="flex items-center gap-[5px]">
                <Icon name="stars" fill={1} />
                <span className="text-xs font-medium text-[var(--label-color)]">
                  Esta é uma campanha padrão da marca {campaign.brand.name}
                </span>
              </div>

              <div className="flex items-center justify-between" ref={campaignHeader}>
                <h2 className="text-primary text-lg font-semibold">{campaign.name}</h2>
              </div>

              <div className="grid grid-cols-2 gap-8">
                <div className="flex flex-col gap-8">
                  <InfoItem
                    label="Formato de exibição"
                    value={campaignDisplayFormatMapper(campaign.displayFormat) || '-'}
                  />
                  <InfoItem
                    label="Marcas vinculadas"
                    value={
                      [campaign.brand.name, ...(campaign.relatedBrands?.map((item) => item.name) ?? [])]
                        .filter(Boolean)
                        .join(', ') || '-'
                    }
                  />
                </div>
                <div className="flex flex-col gap-8">
                  <InfoItem label="Data de início" value={formatDate(campaign.startDate)} />
                  <InfoItem label="Data de Encerramento" value={formatDate(campaign.endDate)} />
                </div>
                <div className="col-span-2">
                  <InfoItem label="Descrição" value={campaign.description} />
                </div>
                {editButton}
                {isMobile && (
                  <Button
                    variant="outline"
                    className="text-primary hover:text-primary flex h-11 gap-2.5 rounded-[5px]"
                    onClick={() => videoTrigger.current?.click()}
                  >
                    <Icon name="auto_read_play" />
                    <span className="text-sm font-semibold">Assistir prévia</span>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="hidden flex-1 rounded-[5px] md:block">
            <CardContent className="p-6">
              <h2 className="text-primary mb-4 text-lg font-semibold">Prévia de campanha</h2>
              <VideoModal
                videos={campaign.medias.map((media) => media.mediaUrl)}
                trigger={
                  <div className="relative mx-auto w-full max-w-2xl cursor-pointer" ref={videoTrigger}>
                    <video
                      src={campaign.medias[0]?.mediaUrl ?? ''}
                      className="max-h-[350px] w-full rounded-lg"
                      preload="metadata"
                      muted
                      playsInline
                      onLoadedMetadata={(e) => {
                        e.currentTarget.currentTime = 1;
                      }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="rounded-full p-4 shadow-lg">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-10 w-10 text-red-600"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                }
              />
            </CardContent>
          </Card>
        </div>

        <section className="flex flex-col gap-8">
          {/* <Summary summaryData={materialSummaryData} title="Status de materiais" /> */}
          <MaterialListFilter
            onChange={(filters) => {
              setFilters(filters);
              setPage(1);
            }}
          />
          <section className="w-full">
            {isLoading && <p className="p-4">Carregando materiais de PDV...</p>}
            {isError && <p className="p-4 text-red-500">Erro ao carregar os materiais de PDV.</p>}
            {!isLoading && !isError && (
              <GenericTable
                columns={columns}
                data={materials}
                onPageChange={setPage}
                itemsPerPages={pageSize}
                totalItems={totalItems}
                page={page}
              />
            )}
          </section>
          <VideoModal videos={videoUrls} trigger={<button ref={videoTriggerRef} className="hidden" />} />
        </section>

        <Card className="rounded-[10px]">
          <CardHeader className="flex justify-between pb-0" ref={campaignMediaHeader}>
            <CardTitle className="text-primary text-lg leading-5 font-semibold">Edição de campanha</CardTitle>
          </CardHeader>
          <CardContent className="mx-4 flex rounded-[10px] bg-[#6D6D6D33] p-4">
            <ScrollArea type="always" className="w-1 flex-1 flex-row flex-nowrap">
              <div className="flex w-full space-x-4 p-4">
                {!!campaign.medias.length &&
                  campaign.medias.map((media) => <MediaCard key={media.uuid} media={media} />)}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </CardContent>
          <CardFooter> {bottomEditButton}</CardFooter>
        </Card>
      </div>
    </BaseLayout>
  );
}
